import { useEffect, useState } from "react";
import { KeyRound, Wifi, ShieldAlert, Plus, Trash2, Save, Loader2, AlertTriangle, MonitorSmartphone } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtDateTime } from "@/lib/format";
import { BanList } from "@/components/security/BanList";
import { confirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";

const Box = ({ icon: Icon, title, hint, children, testid }) => (
  <div className="rounded-xl border p-4 space-y-3" data-testid={testid}>
    <div className="flex items-start gap-2.5">
      <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><Icon className="h-4 w-4" /></span>
      <div><div className="font-semibold text-sm">{title}</div>{hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}</div>
    </div>
    {children}
  </div>
);

export const useSecurityOverview = () => {
  const [ov, setOv] = useState(null);
  const load = () => api.get("/security/overview").then((r) => setOv(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const [busy, setBusy] = useState("");
  const unban = async (b) => {
    if (!await confirm(`${b.ip} ${b.kind === "device" ? "cihaz " : ""}engeli kaldırılsın mı? Bu bağlantıdan yeniden giriş denenebilecek.`)) return;
    setBusy(b.id);
    try { await api.delete(`/security/bans/${b.id}`); toast.success("Engel kaldırıldı"); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return { ov, load, unban, busy };
};

function GateCodeForm({ ov, onSaved }) {
  const [f, setF] = useState({ code: "", code2: "", secret: "" });
  const [saving, setSaving] = useState(false);
  const digits = (v) => v.replace(/\D/g, "").slice(0, 4);
  const save = async () => {
    if (!/^\d{4}$/.test(f.code)) return toast.error("Yeni parola 4 haneli rakam olmalı");
    if (f.code !== f.code2) return toast.error("Parolalar eşleşmiyor");
    if (!f.secret) return toast.error("Onay için kendi şifrenizi veya PIN'inizi girin");
    setSaving(true);
    try { await api.put("/security/gate-code", { code: f.code, admin_secret: f.secret }); toast.success("Giriş parolası değiştirildi"); setF({ code: "", code2: "", secret: "" }); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Box icon={KeyRound} title="Site Giriş Parolası" hint="Kullanıcı seçiminden önce sorulan 4 haneli ortak parola · 3 hatalı denemede bağlantı engellenir" testid="sec-gate-box">
      {ov?.gate?.is_default && <p className="text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 flex items-start gap-1.5" data-testid="sec-gate-default-warning"><AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />Varsayılan parola <b>0000</b> kullanılıyor — değiştirmeniz önerilir.</p>}
      {ov?.gate?.updated_at && <p className="text-[11px] text-muted-foreground">Son değişiklik: {fmtDateTime(ov.gate.updated_at)} · {ov.gate.updated_by}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="space-y-1"><Label className="text-[11px]">Yeni parola</Label><Input type="password" inputMode="numeric" autoComplete="new-password" value={f.code} onChange={(e) => setF({ ...f, code: digits(e.target.value) })} placeholder="••••" className="tracking-[0.5em] text-center font-bold" data-testid="sec-gate-code" /></div>
        <div className="space-y-1"><Label className="text-[11px]">Tekrar</Label><Input type="password" inputMode="numeric" autoComplete="new-password" value={f.code2} onChange={(e) => setF({ ...f, code2: digits(e.target.value) })} placeholder="••••" className="tracking-[0.5em] text-center font-bold" data-testid="sec-gate-code2" /></div>
        <div className="space-y-1"><Label className="text-[11px]">Kendi şifreniz / PIN</Label><Input type="password" autoComplete="current-password" value={f.secret} onChange={(e) => setF({ ...f, secret: e.target.value })} placeholder="Onay" data-testid="sec-gate-secret" /></div>
      </div>
      <Button size="sm" onClick={save} disabled={saving} data-testid="sec-gate-save">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Parolayı Değiştir</Button>
    </Box>
  );
}

function TrustedIps({ ov, onSaved }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(ov?.trusted_ips || []); }, [ov]);
  const set = (i, k, v) => setItems((p) => p.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    setSaving(true);
    try { await api.put("/security/trusted-ips", { items: items.filter((x) => String(x.ip || "").trim()) }); toast.success("Güvenilir ağlar kaydedildi"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const mine = ov?.my_ip;
  return (
    <Box icon={Wifi} title="Güvenilir Ağlar (dükkân interneti)" hint="Dükkânda herkes aynı IP'yi kullanır. Buraya eklenen ağlarda 3 hatalı denemede tüm dükkân değil, yalnız hatayı yapan CİHAZ engellenir. Diğer tüm bağlantılarda IP engellenir." testid="sec-trusted-box">
      {mine && (
        <div className="flex flex-wrap items-center gap-2 text-xs rounded-lg bg-muted/50 px-3 py-2" data-testid="sec-my-ip">
          <MonitorSmartphone className="h-3.5 w-3.5 text-muted-foreground" />Bu cihazın IP adresi: <b className="font-mono">{mine}</b>{ov.my_ip_trusted && <span className="text-emerald-700 font-semibold">· güvenilir</span>}
          {!ov.my_ip_trusted && <Button size="sm" variant="outline" className="h-7 ml-auto" onClick={() => setItems((p) => [...p, { ip: mine, label: "Dükkân" }])} data-testid="sec-add-my-ip"><Plus className="h-3.5 w-3.5 mr-1" />Listeye ekle</Button>}
        </div>
      )}
      <div className="space-y-2">
        {items.map((x, i) => (
          <div key={i} className="flex gap-2" data-testid={`sec-trusted-row-${i}`}>
            <Input value={x.ip} onChange={(e) => set(i, "ip", e.target.value)} placeholder="ör. 88.230.12.45" className="font-mono" data-testid={`sec-trusted-ip-${i}`} />
            <Input value={x.label || ""} onChange={(e) => set(i, "label", e.target.value)} placeholder="Açıklama (ör. Dükkân Wi-Fi)" data-testid={`sec-trusted-label-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => setItems((p) => p.filter((_, j) => j !== i))} data-testid={`sec-trusted-rm-${i}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
          </div>
        ))}
        {!items.length && <p className="text-xs text-muted-foreground">Liste boş — tüm bağlantılarda 3 hatada IP engellenir.</p>}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => setItems((p) => [...p, { ip: "", label: "" }])} data-testid="sec-trusted-add"><Plus className="h-4 w-4 mr-1" />Ağ Ekle</Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid="sec-trusted-save">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Kaydet</Button>
      </div>
    </Box>
  );
}

// Kullanıcılar & Ayarlar › Güvenlik
export default function SecurityPanel() {
  const { ov, load, unban, busy } = useSecurityOverview();
  return (
    <div className="p-5 space-y-4" data-testid="security-panel-body">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <GateCodeForm ov={ov} onSaved={load} />
        <TrustedIps ov={ov} onSaved={load} />
      </div>
      <Box icon={ShieldAlert} title={`Engellenen Bağlantılar${ov?.stats?.active_bans ? ` · ${ov.stats.active_bans} aktif` : ""}`} hint="Giriş parolası veya kullanıcı şifresi/PIN 3 kez hatalı girilen bağlantılar · yanlışlıkla engellendiyse kaldırın" testid="sec-bans-box">
        <BanList bans={ov?.bans} onUnban={unban} busy={busy} />
      </Box>
    </div>
  );
}
